import React from 'react';
import { Box } from '@mui/material';
import hero from "./3dlogo.png";

const DynamicSphere = () => {

  // Orbiting particles (brand-controlled colors)
  const orbitingParticles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    size: Math.random() * 8 + 4,
    delay: Math.random() * 2,
    speed: Math.random() * 20 + 10,
    color: [
      'rgba(255,59,59,0.7)',
      'rgba(88,101,242,0.7)',
      'rgba(255,120,120,0.6)',
    ][Math.floor(Math.random() * 3)]
  }));

  // Floating internal elements
  const internalElements = Array.from({ length: 8 }, (_, i) => ({
    id: i,
    type: i % 3 === 0 ? 'circle' : i % 3 === 1 ? 'triangle' : 'square',
    size: Math.random() * 20 + 10,
    x: Math.random() * 60 - 30,
    y: Math.random() * 60 - 30,
    delay: Math.random() * 5,
    duration: Math.random() * 8 + 4,
    rotation: Math.random() * 360
  }));

  return (
    <Box
      sx={{
        position: 'relative',
        width: { xs: 280, md: 400 },
        height: { xs: 280, md: 400 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >

      {/* Outer Glow Rings */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '2px solid rgba(255,59,59,0.25)',
          animation: 'rotateReverse 20s linear infinite',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '10%',
            borderRadius: '50%',
            border: '1px solid rgba(88,101,242,0.2)',
            animation: 'rotate 15s linear infinite reverse',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: '20%',
            borderRadius: '50%',
            border: '1px solid rgba(255,120,120,0.15)',
            animation: 'rotate 25s linear infinite',
          },
        }}
      />

      {/* Orbiting Particles */}
      {orbitingParticles.map(p => (
        <Box
          key={p.id}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            animation: `orbit${p.id} ${p.speed}s linear ${p.delay}s infinite`,
          }}
        />
      ))}

      {/* Main Sphere */}
      <Box
        sx={{
          position: 'relative',
          width: '80%',
          height: '80%',
          borderRadius: '50%',
          background: `
            linear-gradient(
              135deg,
              #0a0f28 0%,
              #1b1f4a 30%,
              #2b2f7a 55%,
              #ff3b3b 85%,
              #8b0000 100%
            )
          `,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `
            0 0 100px rgba(255,59,59,0.45),
            inset 0 0 80px rgba(255,255,255,0.12),
            inset 0 0 120px rgba(0,0,0,0.6)
          `,
          overflow: 'hidden',

          '&::before': {
            content: '""',
            position: 'absolute',
            top: '10%',
            left: '15%',
            width: '50%',
            height: '50%',
            borderRadius: '50%',
            background: `
              radial-gradient(
                circle,
                rgba(255,59,59,0.25) 0%,
                rgba(88,101,242,0.18) 35%,
                transparent 70%
              )
            `,
            filter: 'blur(20px)',
            animation: 'moveHighlight3D 10s ease-in-out infinite alternate',
          },

          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '15%',
            right: '20%',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: `
              radial-gradient(
                circle,
                rgba(255,59,59,0.18) 0%,
                transparent 70%
              )
            `,
            filter: 'blur(15px)',
            animation: 'moveHighlight3D 8s ease-in-out 2s infinite alternate-reverse',
          },
        }}
      >

        {/* Internal Floating Elements */}
        {internalElements.map(el => (
          <Box
            key={el.id}
            sx={{
              position: 'absolute',
              left: `calc(50% + ${el.x}%)`,
              top: `calc(50% + ${el.y}%)`,
              width: el.size,
              height: el.size,
              borderRadius: el.type === 'circle' ? '50%' : '2px',
              border: el.type !== 'triangle'
                ? '1px solid rgba(255,255,255,0.35)'
                : 'none',
              background: el.type === 'circle'
                ? 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)'
                : 'transparent',
              animation: `floatInternal ${el.duration}s ease-in-out ${el.delay}s infinite`,
              transform: `rotate(${el.rotation}deg)`,

              ...(el.type === 'triangle' && {
                width: 0,
                height: 0,
                borderLeft: `${el.size / 2}px solid transparent`,
                borderRight: `${el.size / 2}px solid transparent`,
                borderBottom: `${el.size}px solid rgba(255,255,255,0.35)`,
              }),
            }}
          />
        ))}

        {/* Pulsing Core */}
        <Box
          sx={{
            position: 'absolute',
            width: '30%',
            height: '30%',
            borderRadius: '50%',
            background: `
              radial-gradient(
                circle,
                rgba(255,59,59,0.35) 0%,
                rgba(88,101,242,0.25) 35%,
                rgba(10,15,40,0.9) 65%,
                transparent 72%
              )
            `,
            filter: 'blur(14px)',
            boxShadow: '0 0 40px rgba(255,59,59,0.5)',
            animation: 'pulseCore 3s ease-in-out infinite',
          }}
        />

        {/* Data Stream */}
        <Box
          sx={{
            position: 'absolute',
            width: '120%',
            height: '120%',
            borderRadius: '50%',
            background:
              'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.1), transparent)',
            animation: 'rotate 8s linear infinite',
            mask: 'radial-gradient(circle, transparent 30%, black 70%)',
          }}
        />

        {/* Logo */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-20%',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(0,0,0,0.6) 0%, transparent 70%)',
              zIndex: -1,
            },
          }}
        >
          <Box
            component="img"
            src={hero}
            alt="Zolang Nexora"
            sx={{
              width: { xs: 90, md: 110 },
              filter: 'drop-shadow(0 0 18px rgba(255,59,59,0.6))',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicSphere;
